import React, { useState, useEffect } from 'react';
import { 
  Sparkles, Copy, Check, ArrowLeft, Brain, Calculator, 
  Star, BookOpen, Info, TrendingUp, Lightbulb, Zap
} from 'lucide-react';

const AIGeneratedPage = ({ workspaceData, solution, onBack }) => {
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Helper function for icon colors
  const getIconColor = (color) => {
    const colorMap = {
      'blue': 'text-blue-600',
      'indigo': 'text-indigo-600',
      'emerald': 'text-emerald-600',
      'amber': 'text-amber-600',
      'violet': 'text-violet-600'
    };
    return colorMap[color] || 'text-gray-600';
  };

  // Generate content sections based on workspace data
  const generateSections = (workspaceData) => {
    const sections = [];
    
    if (workspaceData.length > 0) {
      // Concepts section
      const concepts = workspaceData.filter(item => item.type === 'concept');
      if (concepts.length > 0) {
        sections.push({
          title: 'Mathematical Concepts',
          icon: Brain,
          color: 'indigo',
          items: concepts.map(item => ({
            title: item.name,
            description: item.description,
            formula: item.formula
          }))
        });
      }

      // Formulas section
      const formulas = workspaceData.filter(item => item.type === 'formula' || item.type === 'method');
      if (formulas.length > 0) {
        sections.push({
          title: 'Formulas & Methods',
          icon: Calculator,
          color: 'emerald',
          items: formulas.map(item => ({
            title: item.name,
            description: item.description,
            formula: item.formula
          }))
        });
      }

      // Values section
      const values = workspaceData.filter(item => item.type === 'value' || item.type === 'result');
      if (values.length > 0) {
        sections.push({
          title: 'Values & Results',
          icon: Star,
          color: 'amber',
          items: values.map(item => ({
            title: item.name,
            description: item.description,
            formula: item.formula
          }))
        });
      }

      // Steps section
      const steps = workspaceData.filter(item => item.type === 'step');
      if (steps.length > 0) {
        sections.push({
          title: 'Solution Steps',
          icon: BookOpen,
          color: 'violet',
          items: steps.map(item => ({
            title: item.name,
            description: item.description,
            formula: item.formula
          }))
        });
      }
    }

    return sections;
  };

  // Generate AI recommendations
  const generateRecommendations = (workspaceData) => {
    const recommendations = [];
    
    if (workspaceData.length === 0) {
      recommendations.push({
        icon: Lightbulb,
        title: 'Start Building',
        description: 'Add mathematical tools from the ToolBox to begin your analysis',
        color: 'blue'
      });
    } else {
      recommendations.push({
        icon: TrendingUp,
        title: 'Optimize Your Workflow',
        description: 'Consider adding more analysis tools to enhance your mathematical reasoning',
        color: 'emerald'
      });
      
      recommendations.push({
        icon: Zap,
        title: 'Advanced Techniques',
        description: 'Explore additional mathematical concepts for deeper understanding',
        color: 'violet'
      });
    }

    return recommendations;
  };

  // Generate content when workspace data changes
  useEffect(() => {
    if (workspaceData && workspaceData.length > 0) {
      setIsGenerating(true);
      
      // Simulate AI processing
      setTimeout(() => {
        const sections = generateSections(workspaceData);
        const recommendations = generateRecommendations(workspaceData);
        
        setGeneratedContent({
          summary: `Analysis completed with ${workspaceData.length} mathematical tools. The workspace contains a comprehensive set of concepts, formulas, and solution steps.`,
          sections,
          recommendations,
          metadata: {
            toolsUsed: workspaceData.length,
            concepts: workspaceData.filter(item => item.type === 'concept').length,
            formulas: workspaceData.filter(item => item.type === 'formula' || item.type === 'method').length,
            steps: workspaceData.filter(item => item.type === 'step').length,
            generatedAt: new Date().toLocaleString()
          }
        });
        setIsGenerating(false);
      }, 2000);
    } else {
      setGeneratedContent(null);
    }
  }, [workspaceData]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Solver"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">AI Generated Analysis</h1>
              <p className="text-sm text-gray-600">Intelligent Mathematical Insights</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">AI Powered</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isGenerating ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Generating Analysis...</h3>
              <p className="text-gray-600">AI is processing your mathematical workspace</p>
            </div>
          </div>
        ) : generatedContent ? (
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Summary Section */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-xl">
                  <Brain className="w-8 h-8 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Analysis Summary</h2>
                  <p className="text-gray-600">AI-generated insights from your workspace</p>
                </div>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">{generatedContent.summary}</p>
              
              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{generatedContent.metadata.toolsUsed}</div>
                  <div className="text-sm text-gray-600">Tools Used</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{generatedContent.metadata.concepts}</div>
                  <div className="text-sm text-gray-600">Concepts</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600">{generatedContent.metadata.formulas}</div>
                  <div className="text-sm text-gray-600">Formulas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-violet-600">{generatedContent.metadata.steps}</div>
                  <div className="text-sm text-gray-600">Steps</div>
                </div>
              </div>
            </div>

            {/* Generated Sections */}
            {generatedContent.sections.map((section, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`p-3 rounded-xl ${getIconColor(section.color).replace('text-', 'bg-').replace('-600', '-100')}`}>
                    <section.icon className={`w-8 h-8 ${getIconColor(section.color)}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                    <p className="text-gray-600">AI-analyzed mathematical components</p>
                  </div>
                </div>
                
                <div className="grid gap-6">
                  {section.items.map((item, itemIndex) => (
                    <div key={itemIndex} className="bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
                        <button
                          onClick={() => handleCopy(`${item.title}\n\n${item.description}${item.formula ? `\n\nFormula: ${item.formula}` : ''}`)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Copy to clipboard"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                        </button>
                      </div>
                      <p className="text-gray-700 mb-4 leading-relaxed">{item.description}</p>
                      {item.formula && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="text-sm font-semibold text-blue-800 mb-2">Mathematical Formula:</div>
                          <div className="text-blue-700 font-mono text-lg">{item.formula}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* AI Recommendations */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl">
                  <Lightbulb className="w-8 h-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">AI Recommendations</h3>
                  <p className="text-gray-600">Intelligent suggestions for your analysis</p>
                </div>
              </div>
              
              <div className="grid gap-4">
                {generatedContent.recommendations.map((rec, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                    <div className={`p-2 rounded-lg ${getIconColor(rec.color).replace('text-', 'bg-').replace('-600', '-100')}`}>
                      <rec.icon className={`w-5 h-5 ${getIconColor(rec.color)}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{rec.title}</h4>
                      <p className="text-gray-700">{rec.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Metadata Footer */}
            <div className="bg-gray-50 rounded-xl p-6 text-center">
              <p className="text-sm text-gray-600">
                Generated on {generatedContent.metadata.generatedAt} • 
                AI Analysis powered by Mathematical Intelligence
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center max-w-md">
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-gray-200">
                <Sparkles className="w-20 h-20 mx-auto mb-6 opacity-40 text-gray-400" />
                <h3 className="text-xl font-semibold text-gray-700 mb-3">No Analysis Available</h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Use the ToolBox to create a mathematical workspace, then run the analysis to generate AI insights.
                </p>
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                  <span>Build your workspace first</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIGeneratedPage; 